/**
 * Мультипоиск на настоящем API: подсказки — `GET /catalog/products?q=`,
 * наличие всего выбранного — одним запросом `GET /catalog/availability?ids=`.
 * Пересечение с учётом количества считает экран (`model.ts`).
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { fetchProducts, type Availability } from '../catalog/api'
import { match, type Match, type Picked } from './model'

/** С какой длины строки спрашивать сервер: по одной букве подсказки бесполезны. */
export const MIN_QUERY = 2

/** Подсказки по строке поиска — одна страница, популярные выше. */
export function useSuggestions(q: string, lang: string) {
  const api = useApi()
  const query = q.trim()
  return useQuery({
    queryKey: keys.suggest(query, lang),
    queryFn: async () => (await fetchProducts(api, { q: query, sort: 'popular' }, lang)).items,
    enabled: query.length >= MIN_QUERY,
  })
}

/** Сколько номеров принимает пакетная ручка наличия за раз. */
export const BATCH_MAX = 50

type StoreMatchDto = components['schemas']['StoreMatch']

/**
 * Пакетный ответ — по аптекам; экрану нужно наличие по каждому лекарству,
 * как у карточки товара. У каждого спрошенного номера есть строка в ответе,
 * пусть пустая: «нигде нет» отличается от «ещё не спрашивали».
 */
export function stockByProduct(
  ids: number[],
  stores: StoreMatchDto[],
): Map<number, Availability[]> {
  const byProduct = new Map<number, Availability[]>(ids.map((id) => [id, []]))
  for (const store of stores) {
    for (const row of store.items ?? []) {
      const available = row.available ?? 0
      byProduct.get(row.product_id ?? 0)?.push({
        storeId: store.store_id ?? 0,
        storeName: store.store ?? '',
        district: store.district ?? '',
        qty: available,
        available,
        price: row.price ?? 0,
      })
    }
  }
  return byProduct
}

/** Наличие выбранного — `GET /catalog/availability?ids=`, по 50 номеров за запрос. */
export async function fetchSelectionStock(
  api: ApiClient,
  ids: number[],
): Promise<Map<number, Availability[]>> {
  const chunks: number[][] = []
  for (let at = 0; at < ids.length; at += BATCH_MAX) chunks.push(ids.slice(at, at + BATCH_MAX))
  const pages = await Promise.all(
    chunks.map((chunk) =>
      api.get<{ items?: StoreMatchDto[] }>('/catalog/availability', {
        query: { ids: chunk.join(',') },
      }),
    ),
  )
  return stockByProduct(
    ids,
    pages.flatMap((page) => page.items ?? []),
  )
}

/** Наличие одного выбранного: строка «есть в N аптеках» и её повтор. */
export interface StockState {
  isPending: boolean
  isError: boolean
  error: unknown
  /** Строки наличия; пока ответа нет — пусто, смотреть `isPending`. */
  data: Availability[]
  refetch: () => Promise<unknown>
}

export interface ItemState {
  item: Picked
  query: StockState
}

export interface SelectionMatch {
  /** Наличие каждого выбранного — для строк «есть в N аптеках». */
  states: ItemState[]
  /** Наличие ещё едет (или едет новое после изменения выбора). */
  pending: boolean
  /** Почему наличие не пришло; null — пришло или ещё едет. */
  error: unknown
  retry: () => void
  /** Пересечение, когда пришло всё; иначе null. */
  result: Match | null
}

/**
 * Выбор → один запрос наличия → пересечение. Пока после нового лекарства едет
 * новый ответ, уже проверенные строки показывают прежний: список не мигает.
 * Количество на запрос не влияет — его учитывает пересечение.
 */
export function useSelectionMatch(items: Picked[]): SelectionMatch {
  const api = useApi()
  const ids = [...new Set(items.map((item) => item.id))].sort((a, b) => a - b)
  const stock = useQuery({
    queryKey: keys.multiAvailability(ids),
    queryFn: () => fetchSelectionStock(api, ids),
    enabled: ids.length > 0,
    placeholderData: keepPreviousData,
  })
  const stale = stock.isPlaceholderData
  const states = items.map((item) => {
    const rows = stock.data?.get(item.id)
    return {
      item,
      query: {
        isPending: stock.isPending || (stale && rows === undefined),
        isError: stock.isError,
        error: stock.error,
        data: rows ?? [],
        refetch: stock.refetch,
      },
    }
  })
  const pending = items.length > 0 && (stock.isPending || stale)
  const ready = items.length > 0 && !pending && !stock.isError
  return {
    states,
    pending,
    error: stock.isError ? stock.error : null,
    retry: () => {
      void stock.refetch()
    },
    result: ready ? match(states.map(({ item, query }) => ({ item, rows: query.data }))) : null,
  }
}
